import { useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadWebsiteBrightDataProps extends UploadComponentProps {
  websiteUrl: string;
  onWebsiteUrlChange: (url: string) => void;
}

const UploadWebsiteBrightData = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  websiteUrl,
  onWebsiteUrlChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadWebsiteBrightDataProps) => {
  const websiteExtractMutation = useUploadMutation(
    "website-brightdata",
    selectedAgentId,
    onUploadSuccess,
  );

  const handleSubmit = useCallback(() => {
    if (!websiteUrl.trim()) {
      toast.error("Please enter a website URL");
      return;
    }

    if (onDeferredActionAdd) {
      onDeferredActionAdd({
        type: "website",
        data: {
          url: websiteUrl,
          fileName: `Website: ${websiteUrl}`,
        },
      });
      toast.success(`Website content will be extracted after genie creation`);
      onWebsiteUrlChange("");
    } else {
      websiteExtractMutation.mutate({ url: websiteUrl });
    }
  }, [websiteUrl, onWebsiteUrlChange, onDeferredActionAdd, websiteExtractMutation]);

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="globe" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Extract Website Content (Alternate)</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">Scrape and extract text content from any public website using an alternate extraction engine</p>

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Input
            type="url"
            placeholder="https://example.com/article"
            value={websiteUrl}
            onChange={(e) => onWebsiteUrlChange(e.target.value)}
            className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
            disabled={websiteExtractMutation.isPending}
          />
          <Button
            onClick={handleSubmit}
            disabled={!websiteUrl || websiteExtractMutation.isPending}
            className="w-full sm:w-auto bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-6 sm:px-8 h-11 sm:h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {websiteExtractMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Extract"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default UploadWebsiteBrightData;
